import { useState } from "react";
import { calculateTCO, calculateIndicativeTCO, formatEurCompact, CONSTRUCTION_COSTS } from "../lib/tco.js";

// Modus:
//   "listing"   — per concrete listing met grondoppervlak + bouwprijs uit data
//   "town"      — indicatieve calc per concelho (geen specifiek pand)
export function TcoBreakdown({ listing, concelho, mode = "listing" }) {
  const [level, setLevel] = useState("typical");
  const [needsRoad, setNeedsRoad] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const tco = mode === "town"
    ? calculateIndicativeTCO(concelho)
    : calculateTCO(listing, concelho, { renovationLevel: level, needsNewRoad: needsRoad });

  if (!tco || !tco.total) return null;

  const segments = [
    { label: "Koop",          value: tco.price,            color: "#3d6e8a" },
    { label: "Belastingen",   value: tco.acquisition,      color: "#8a6b4a" },
    { label: "Renovatie",     value: tco.renovation,       color: "#d4a851" },
    { label: "Architect",     value: tco.architect,        color: "#a08560" },
    { label: "Vergunningen",  value: tco.permits,          color: "#7a8a48" },
    { label: "Infrastructuur",value: tco.infrastructure.total, color: "#5a8268" },
    { label: "Onverwacht",    value: tco.contingency,      color: "#b85c4d" },
  ].filter(s => s.value > 0);

  return (
    <section className="tco">
      <header className="tco-head">
        <h3>
          {mode === "town" ? "Indicatieve TCO" : "Wat kost dit pand écht?"}
        </h3>
        <p className="tco-sub">
          {mode === "town"
            ? `Voorbeeld: woning €600k + 150 m² renovatie in deze regio.`
            : <>Totale eigendoms-kosten — niet alleen de koopprijs.
                Zoals Tiago Máximo schrijft: <em>"de koopprijs is vaak het kleinste deel"</em>.</>}
        </p>
      </header>

      {/* Totaal-bar */}
      <div className="tco-total-row">
        <div className="tco-total-num">{formatEurCompact(tco.total)}</div>
        <div className="tco-total-meta">
          <span>Totaal realistisch budget</span>
          <small>+{tco.onTopPct}% bovenop koopprijs van {formatEurCompact(tco.price)}</small>
        </div>
      </div>

      {/* Visualisatie als gestapelde bar */}
      <div className="tco-stacked-bar" aria-label="Verdeling van kosten">
        {segments.map((s, i) => {
          const pct = (s.value / tco.total) * 100;
          if (pct < 0.5) return null;
          return (
            <div
              key={i}
              className="tco-seg"
              style={{ width: `${pct}%`, background: s.color }}
              title={`${s.label}: ${formatEurCompact(s.value)} (${pct.toFixed(0)}%)`}
            />
          );
        })}
      </div>
      <div className="tco-legend">
        {segments.map((s, i) => (
          <div key={i} className="tco-legend-item">
            <span className="tco-dot" style={{ background: s.color }} />
            <span className="tco-leg-label">{s.label}</span>
            <span className="tco-leg-value">{formatEurCompact(s.value)}</span>
          </div>
        ))}
      </div>

      {/* Listing-mode: interactieve sliders */}
      {mode === "listing" && (
        <div className="tco-options">
          <div className="tco-opt-row">
            <span className="tco-opt-label">Afwerkingsniveau</span>
            <div className="tco-opt-buttons">
              {[
                ["min",     "Basis"],
                ["typical", "Standaard"],
                ["premium", "Premium"],
              ].map(([v, lbl]) => (
                <button
                  key={v}
                  className={`tco-opt-btn ${level === v ? "active" : ""}`}
                  onClick={() => setLevel(v)}
                  type="button"
                >{lbl}</button>
              ))}
            </div>
          </div>
          <div className="tco-opt-row">
            <label className="tco-opt-check">
              <input type="checkbox" checked={needsRoad} onChange={e => setNeedsRoad(e.target.checked)} />
              <span>Slechte toegangsweg — nieuw grondwerk nodig (+€11k)</span>
            </label>
          </div>
        </div>
      )}

      {/* Details collapsible */}
      <details className="tco-detail" open={showDetail} onToggle={e => setShowDetail(e.target.open)}>
        <summary>Detail-uitsplitsing &amp; jaarlijkse kosten</summary>
        <div className="tco-detail-body">
          <h4>Aankoop-belastingen</h4>
          <ul className="tco-kv">
            <li><span>IMT (overdrachtsbelasting)</span><b>{formatEurCompact(tco.acquisitionDetail.imt)}</b></li>
            <li><span>Imposto do Selo (zegelrecht, 0,8%)</span><b>{formatEurCompact(tco.acquisitionDetail.stamp)}</b></li>
            <li><span>Registratie</span><b>{formatEurCompact(tco.acquisitionDetail.registration)}</b></li>
            <li><span>Solicitor / advogado</span><b>{formatEurCompact(tco.acquisitionDetail.solicitorFees)}</b></li>
          </ul>

          <h4>Bouw / renovatie</h4>
          <ul className="tco-kv">
            <li><span>€/m² ({tco.renovationDetail.region} · {tco.renovationDetail.level})</span><b>€{tco.renovationDetail.ppsm}/m²</b></li>
            <li><span>Bebouwd oppervlak</span><b>{tco.renovationDetail.builtArea} m²</b></li>
            <li><span>Renovatie totaal</span><b>{formatEurCompact(tco.renovation)}</b></li>
            <li><span>Architect (~10% bouwkost)</span><b>{formatEurCompact(tco.architect)}</b></li>
            <li><span>Vergunningen + gemeente</span><b>{formatEurCompact(tco.permits)}</b></li>
          </ul>

          <h4>Infrastructuur</h4>
          <ul className="tco-kv">
            <li><span>Water (boorput + pomp)</span><b>{formatEurCompact(tco.infrastructure.water)}</b></li>
            <li><span>Elektriciteit-aansluiting</span><b>{formatEurCompact(tco.infrastructure.electric)}</b></li>
            <li><span>Septic + drainage</span><b>{formatEurCompact(tco.infrastructure.septic)}</b></li>
            <li><span>Toegangsweg</span><b>{formatEurCompact(tco.infrastructure.road)}</b></li>
          </ul>

          <h4>Onverwachte kosten</h4>
          <ul className="tco-kv">
            <li><span>10% contingency op renovatie + infra</span><b>{formatEurCompact(tco.contingency)}</b></li>
          </ul>

          <h4>Jaarlijkse kosten (na koop)</h4>
          <ul className="tco-kv">
            <li><span>IMI (gemeentebelasting)</span><b>{formatEurCompact(tco.yearly.imi)} <small>/jr</small></b></li>
            <li><span>Verzekeringen</span><b>{formatEurCompact(tco.yearly.insurance)} <small>/jr</small></b></li>
            <li><span>Onderhoud (1% pandwaarde)</span><b>{formatEurCompact(tco.yearly.maintenance)} <small>/jr</small></b></li>
            <li><span>Nutsvoorzieningen</span><b>{formatEurCompact(tco.yearly.utilities)} <small>/jr</small></b></li>
            <li className="tco-kv-total"><span>Jaarlijks totaal</span><b>{formatEurCompact(tco.yearly.total)} <small>/jr</small></b></li>
          </ul>

          <p className="tco-disclaimer">
            Indicatief budget op basis van publieke tarieven en regio-gemiddelden.
            IMT-tarieven verschillen voor hoofd- vs tweede verblijf en wijzigen jaarlijks —
            laat exacte cijfers altijd door je advogado bevestigen.
          </p>
        </div>
      </details>
    </section>
  );
}
